// Ubicación en el repo: src/components/QuotesList.jsx
// Listado de cotizaciones de audífonos (migración 0051, 26-ago-2026) —
// sub-pestaña "Cotizaciones" de HearingAids.jsx. Mismo patrón de
// tabla+búsqueda que el resto de listados del sistema.
import { useCallback, useEffect, useState } from 'react';
import { listQuotes, getQuoteWithItems, softDeleteQuote } from '../lib/hearingaidQuotes';
import { getSignedCatalogImageUrl } from '../lib/catalogImages';
import { getBranchLogoUrl } from '../lib/branchLogos';
import { hearingaidQuoteHTML } from '../lib/receipts';
import { printHTML } from '../lib/print';
import { formatPatientName, formatDate, formatDateTime } from '../lib/format';
import QuoteFormModal from './QuoteFormModal';
import Spinner from './Spinner';

export default function QuotesList({ branchId, activeBranch, catalog, actorId }) {
  const [search, setSearch] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [printingId, setPrintingId] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const refresh = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError('');
    try {
      setQuotes(await listQuotes({ branchId, search }));
    } catch (err) {
      setError(err.message || 'No se pudo cargar las cotizaciones.');
    } finally {
      setLoading(false);
    }
  }, [branchId, search]);

  useEffect(() => {
    const t = setTimeout(refresh, 300);
    return () => clearTimeout(t);
  }, [refresh]);

  function handleNew() {
    setEditTarget(null);
    setFormOpen(true);
  }

  async function handleEdit(q) {
    try {
      const full = await getQuoteWithItems(q.id);
      setEditTarget(full);
      setFormOpen(true);
    } catch (err) {
      setError(err.message || 'No se pudo cargar la cotización.');
    }
  }

  async function handleDelete(q) {
    const name = q.patients ? formatPatientName(q.patients) : q.prospect_name;
    const ok = window.confirm(`¿Eliminar la cotización de ${name}?`);
    if (!ok) return;
    try {
      await softDeleteQuote(q.id, actorId);
      refresh();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la cotización.');
    }
  }

  async function handlePrint(q) {
    setPrintingId(q.id);
    setError('');
    try {
      const full = await getQuoteWithItems(q.id);
      const [logoUrl, itemsWithImages] = await Promise.all([
        getBranchLogoUrl(activeBranch),
        Promise.all(
          full.items.map(async (it) => ({ ...it, imageUrl: await getSignedCatalogImageUrl(it.catalog?.image_path) }))
        ),
      ]);
      const html = hearingaidQuoteHTML(full, itemsWithImages, logoUrl);
      const name = full.patients ? formatPatientName(full.patients) : full.prospect_name;
      printHTML(`Cotizacion - ${name}`, html);
    } catch (err) {
      setError(err.message || 'No se pudo generar la cotización para imprimir.');
    } finally {
      setPrintingId(null);
    }
  }

  function handleFormSaved() {
    setFormOpen(false);
    setEditTarget(null);
    refresh();
  }

  return (
    <div>
      <div className="patients-page__toolbar">
        <h2 style={{ margin: 0 }}>Cotizaciones</h2>
        <div className="patients-page__toolbar-right">
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por nombre…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="button-primary" onClick={handleNew}>
            + Nueva cotización
          </button>
        </div>
      </div>

      <p className="field-hint">
        Cotización referencial imprimible para un paciente con ficha o un prospecto sin ella — no descuenta
        stock ni registra una venta.
      </p>

      {error && <p className="auth-error">{error}</p>}

      <table className="patients-table">
        <thead>
          <tr>
            <th>Para</th>
            <th>Creada</th>
            <th>Válida hasta</th>
            <th>Atendido por</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={5}><Spinner inline /></td>
            </tr>
          ) : quotes.length === 0 ? (
            <tr>
              <td colSpan={5}>No hay cotizaciones registradas.</td>
            </tr>
          ) : (
            quotes.map((q) => {
              const name = q.patients ? formatPatientName(q.patients) : q.prospect_name;
              const isProspect = !q.patient_id;
              return (
                <tr key={q.id}>
                  <td>
                    {name}
                    {isProspect && (
                      <span className="badge badge-neutral" style={{ marginLeft: 6 }}>
                        Prospecto
                      </span>
                    )}
                  </td>
                  <td>{formatDateTime(q.created_at)}</td>
                  <td>{q.valid_until ? formatDate(q.valid_until) : '—'}</td>
                  <td>
                    {q.professionals ? `${q.professionals.nombre} ${q.professionals.apellido}` : '—'}
                  </td>
                  <td className="actions-cell">
                    <button
                      type="button"
                      className="link-button"
                      disabled={printingId === q.id}
                      onClick={() => handlePrint(q)}
                    >
                      🖨 {printingId === q.id ? 'Generando…' : 'Imprimir'}
                    </button>
                    <button type="button" className="link-button" onClick={() => handleEdit(q)}>
                      Editar
                    </button>
                    <button type="button" className="link-button link-button--danger" onClick={() => handleDelete(q)}>
                      Eliminar
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {formOpen && (
        <QuoteFormModal
          quote={editTarget}
          branchId={branchId}
          catalog={catalog}
          actorId={actorId}
          onClose={() => {
            setFormOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleFormSaved}
        />
      )}
    </div>
  );
}
